import React, { useState, useEffect, useRef } from 'react';
import ChargerOnboarding from './ChargerOnboarding';
import { 
  Zap, 
  MapPin, 
  Settings, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Power, 
  Wifi, 
  WifiOff,
  Battery,
  Gauge,
  Wrench,
  Eye,
  MoreVertical,
  Play,
  Square,
  RefreshCw,
  Download,
  Upload,
  Filter,
  Search,
  Plus,
  Edit
} from 'lucide-react';

// Leaflet map component
const MapView = ({ stations, getStatusColor, onStationClick }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    let L;
    try {
      L = require('leaflet');
    } catch (e) {
      return;
    }

    if (!mapRef.current || mapInstanceRef.current) return;

    // Add leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Default center on UK
    const center = stations.length > 0
      ? [stations[0].coordinates.lat, stations[0].coordinates.lng]
      : [51.5074, -0.1278];

    const map = L.map(mapRef.current).setView(center, 13);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    // Add markers for each station
    stations.forEach(station => {
      const color = station.status === 'online' ? '#10B981' : '#EF4444';
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:28px;height:28px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
        </div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14]
      });

      const marker = L.marker([station.coordinates.lat, station.coordinates.lng], { icon }).addTo(map);
      // Hover tooltip only — a click-triggered Leaflet popup here used to fight with the
      // app's own detail modal (marker.on('click') below opens both at once), and Leaflet's
      // popup pane sits at a higher default z-index than the modal, so the modal rendered
      // underneath it and was hard to close. Quick-glance info stays on hover; click opens
      // only the real modal now.
      marker.bindTooltip(`
        <div style="min-width:180px;font-family:system-ui,sans-serif;">
          <strong style="font-size:14px;">${station.name}</strong><br/>
          <span style="color:#6B7280;font-size:12px;">${station.location}</span><br/>
          <span style="display:inline-block;margin-top:6px;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;background:${station.status === 'online' ? '#D1FAE5' : '#FEE2E2'};color:${station.status === 'online' ? '#065F46' : '#991B1B'};">${station.status}</span>
          <div style="margin-top:8px;font-size:12px;color:#4B5563;">
            ${station.powerRating}kW · ${station.connectorType}<br/>
            ${station.totalSessions} sessions · £${station.totalRevenue.toFixed(2)}
          </div>
        </div>
      `, { direction: 'top', offset: [0, -14] });
      marker.on('click', () => onStationClick && onStationClick(station));
    });

    // Fit bounds if multiple stations
    if (stations.length > 1) {
      const bounds = L.latLngBounds(stations.map(s => [s.coordinates.lat, s.coordinates.lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [stations, getStatusColor, onStationClick]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div ref={mapRef} style={{ height: '500px', width: '100%' }} />
      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
        <span className="text-sm text-gray-600">{stations.length} station{stations.length !== 1 ? 's' : ''} shown</span>
        <div className="flex items-center space-x-4 text-xs">
          <span className="flex items-center"><span className="w-3 h-3 rounded-full bg-green-500 mr-1"></span> Online</span>
          <span className="flex items-center"><span className="w-3 h-3 rounded-full bg-red-500 mr-1"></span> Offline</span>
        </div>
      </div>
    </div>
  );
};

const ChargingStationManagement = () => {
  const [selectedStation, setSelectedStation] = useState(null);
  const [diagnosticsStatus, setDiagnosticsStatus] = useState(null);
  const [openIssueCounts, setOpenIssueCounts] = useState({});
  const [chargerIssues, setChargerIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', location: '', price_per_kwh: '', latitude: '', longitude: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editGeocoding, setEditGeocoding] = useState(false);
  const [editGeocodeMessage, setEditGeocodeMessage] = useState(null);

  const openEditModal = (station) => {
    setEditingStation(station);
    setEditForm({
      name: station.name || '',
      location: station.location || '',
      price_per_kwh: station.price_per_kwh != null ? String(station.price_per_kwh) : '',
      latitude: station.coordinates && station.coordinates.lat != null ? String(station.coordinates.lat) : '',
      longitude: station.coordinates && station.coordinates.lng != null ? String(station.coordinates.lng) : '',
    });
    setEditError(null);
    setEditGeocodeMessage(null);
    setShowEditModal(true);
  };

  const handleEditLookupCoordinates = async () => {
    if (!editForm.location.trim()) {
      setEditGeocodeMessage({ type: 'error', text: 'Enter a location/address first' });
      return;
    }
    setEditGeocoding(true);
    setEditGeocodeMessage(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin/geocode?address=${encodeURIComponent(editForm.location)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setEditForm(prev => ({ ...prev, latitude: data.latitude.toFixed(6), longitude: data.longitude.toFixed(6) }));
        setEditGeocodeMessage({ type: 'success', text: `Found: ${data.formatted_address}` });
      } else {
        setEditGeocodeMessage({ type: 'error', text: data.error || 'Could not find that location' });
      }
    } catch (err) {
      setEditGeocodeMessage({ type: 'error', text: 'Lookup failed — check your connection' });
    }
    setEditGeocoding(false);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingStation(null);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingStation) return;
    const payload = {};
    if (editForm.name.trim() !== (editingStation.name || '')) payload.name = editForm.name.trim();
    if (editForm.location.trim() !== (editingStation.location || '')) payload.location = editForm.location.trim();
    const newPriceStr = editForm.price_per_kwh.trim();
    const oldPriceStr = editingStation.price_per_kwh != null ? String(editingStation.price_per_kwh) : '';
    if (newPriceStr !== oldPriceStr) {
      const p = parseFloat(newPriceStr);
      if (newPriceStr !== '' && (isNaN(p) || p < 0 || p > 5)) {
        setEditError('Price must be a number between 0 and 5');
        return;
      }
      payload.price_per_kwh = newPriceStr === '' ? 0 : p;
    }
    const oldLat = editingStation.coordinates && editingStation.coordinates.lat != null ? String(editingStation.coordinates.lat) : '';
    const oldLng = editingStation.coordinates && editingStation.coordinates.lng != null ? String(editingStation.coordinates.lng) : '';
    if (editForm.latitude.trim() !== oldLat) {
      const lat = parseFloat(editForm.latitude.trim());
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setEditError('Latitude must be a number between -90 and 90');
        return;
      }
      payload.latitude = lat;
    }
    if (editForm.longitude.trim() !== oldLng) {
      const lng = parseFloat(editForm.longitude.trim());
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setEditError('Longitude must be a number between -180 and 180');
        return;
      }
      payload.longitude = lng;
    }
    if (Object.keys(payload).length === 0) {
      closeEditModal();
      return;
    }
    setSavingEdit(true);
    setEditError(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/chargers/${editingStation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        closeEditModal();
        window.location.reload();
      } else {
        setEditError(data.error || 'Failed to save changes');
      }
    } catch (e) {
      setEditError('Network error. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commandLoading, setCommandLoading] = useState(false);
  const [commandTarget, setCommandTarget] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterConnector, setFilterConnector] = useState('all');
  const [filterMinPower, setFilterMinPower] = useState('');
  // Export charger data as CSV
  const handleExport = () => {
    const headers = ['Name', 'Serial Number', 'Location', 'Status', 'Availability', 'Power (kW)', 'Connector', 'Price/kWh', 'Sessions', 'Revenue (GBP)'];
    const csvRows = [headers.join(',')];
    filteredStations.forEach(s => {
      csvRows.push([
        '"' + s.name + '"',
        s.serialNumber,
        '"' + s.location + '"',
        s.status,
        s.availability,
        s.powerRating,
        s.connectorType,
        s.pricePerKwh,
        s.totalSessions,
        s.totalRevenue.toFixed(2)
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'LMPEnergy-Chargers.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleMoreFilters = () => {
    setShowFilterPanel(!showFilterPanel);
  };

  // OCPP command handler
  const handleCommand = async (stationId, command) => {
    setCommandLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/chargers/${stationId}/command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ command })
      });
      const data = await response.json();
      if (response.ok) {
        alert(`Command "${command}" sent successfully to charger ${stationId}.\n${data.message || ''}`);
        setShowCommandModal(false);
        window.location.reload();
      } else {
        alert('Command failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Command error:', error);
      alert('Failed to send command');
    } finally {
      setCommandLoading(false);
    }
  };

  const handleSettings = (stationId) => {
    setCommandTarget(stations.find(s => s.id === stationId));
    setShowCommandModal(true);
  };

  const handleMaintenance = (stationId) => {
    if (window.confirm(`Put charger ${stationId} into maintenance mode?`)) {
      handleCommand(stationId, 'maintenance_mode');
    }
  };

  // Diagnostics ("Download Logs") — OCPP GetDiagnostics. The charger uploads its file
  // asynchronously after acknowledging the request, so this is check-status-on-demand,
  // not instant. Only actually verified against our own upload/download endpoints in
  // testing — whether the physical BSEC charger's firmware honours GetDiagnostics with
  // an HTTP upload location (vs. FTP, or not supporting it at all) is unconfirmed.
  const fetchDiagnosticsStatus = async (stationId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/chargers/${stationId}/diagnostics`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const data = await response.json();
        setDiagnosticsStatus((data.requests && data.requests[0]) || null);
      }
    } catch (error) {
      console.error('Error checking diagnostics status:', error);
    }
  };

  const handleRequestDiagnostics = async (stationId) => {
    setDiagnosticsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/chargers/${stationId}/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ command: 'get_diagnostics' })
      });
      const result = await response.json();
      if (result.ocpp_connected === false) {
        alert('Charger is not currently connected via OCPP — the diagnostics request could not be delivered.');
      }
      await fetchDiagnosticsStatus(stationId);
    } catch (error) {
      console.error('Error requesting diagnostics:', error);
      alert('Failed to request diagnostics. Please try again.');
    }
    setDiagnosticsLoading(false);
  };

  const handleDownloadDiagnostics = async (diagnosticId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/chargers/diagnostics/${diagnosticId}/download`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `charger-diagnostics-${diagnosticId}.bin`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to download diagnostics file.');
      }
    } catch (error) {
      console.error('Error downloading diagnostics:', error);
      alert('Failed to download diagnostics file.');
    }
  };

  // Save charger configuration
  const handleSaveConfig = async (stationId, config) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/chargers/${stationId}/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          price_per_kwh: config.energyPrice,
          location: config.location,
          latitude: config.latitude,
          longitude: config.longitude
        })
      });
      if (response.ok) {
        alert('Configuration saved');
        window.location.reload();
      } else {
        const data = await response.json();
        alert('Save failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Config save error:', error);
      alert('Failed to save configuration');
    }
  };


  useEffect(() => {
    setDiagnosticsStatus(null);
    if (selectedStation?.id) fetchDiagnosticsStatus(selectedStation.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation?.id]);

  const fetchOpenIssueCounts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/admin/charger-issues/open-counts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const map = {};
        (data.counts || []).forEach(c => { map[c.charger_id] = c.open_count; });
        setOpenIssueCounts(map);
      }
    } catch (error) {
      console.error('Error fetching open issue counts:', error);
    }
  };

  const fetchChargerIssues = async (stationId) => {
    setIssuesLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin/charger-issues?charger_id=${stationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChargerIssues(data.reports || []);
      }
    } catch (error) {
      console.error('Error fetching charger issues:', error);
    }
    setIssuesLoading(false);
  };

  const updateIssueStatus = async (issueId, status) => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`/api/admin/charger-issues/${issueId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      if (selectedStation) fetchChargerIssues(selectedStation.id);
      fetchOpenIssueCounts();
    } catch (error) {
      console.error('Error updating issue status:', error);
    }
  };

  useEffect(() => {
    setChargerIssues([]);
    if (selectedStation?.id) fetchChargerIssues(selectedStation.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation?.id]);

  useEffect(() => {
    fetchOpenIssueCounts();
    const interval = setInterval(fetchOpenIssueCounts, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadStationsData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch("/api/chargers/live-status", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        if (response.ok) {
          const chargersResponse = await response.json();
          const chargersData = chargersResponse.chargers || chargersResponse;
          
          const formattedStations = chargersData.map((charger) => {
            // Determine availability correctly:
            // - offline charger = unavailable (not "in-use")
            // - online + not available = in-use (actively charging)
            // - online + available = available
            let availability = "unavailable";
            if (charger.ocpp_connected) {
              availability = charger.is_available ? "available" : "in-use";
            }
            
            return {
              id: charger.id,
              name: charger.name,
              serialNumber: charger.serial_number || `SK-${charger.id}`,
              location: charger.location,
              address: charger.location,
              coordinates: {
                lat: parseFloat(charger.latitude) || 51.5074,
                lng: parseFloat(charger.longitude) || -0.1278
              },
              status: charger.ocpp_connected ? "online" : "offline",
              availability: availability,
              powerRating: charger.power_rating / 1000 || 22,
              connectorType: charger.connector_type || "Type 2",
              pricePerKwh: charger.price_per_kwh || 0.35,
              firmwareVersion: charger.ocpp_firmware_version || "Unknown",
              lastHeartbeat: charger.last_heartbeat ? new Date(charger.last_heartbeat) : null,
              // NOTE: still "is it connected right now" (99/0), not real historical uptime — there's
              // no time-series connectivity log anywhere in the system to compute a genuine uptime %
              // from. Flagging rather than faking a fix; would need a new tracking table + cron job.
              uptime: charger.ocpp_connected ? 99 : 0,
              totalSessions: parseInt(charger.total_sessions) || 0,
              totalRevenue: parseFloat(charger.total_revenue) || 0,
              currentSession: charger.current_session ? {
                id: charger.current_session.id,
                userName: charger.current_session.user_name,
                currentPower: charger.current_session.current_power != null ? (charger.current_session.current_power / 1000).toFixed(1) : 'Unknown',
                energyDelivered: charger.current_session.energy_delivered != null ? (charger.current_session.energy_delivered / 1000).toFixed(2) : 'Unknown',
              } : null,
              diagnostics: {
                powerOutput: charger.current_session?.current_power != null ? charger.current_session.current_power / 1000 : null,
                voltage: charger.voltage,
                current: charger.current,
                faultCodes: charger.fault_codes || [],
                lastMaintenance: null,
                nextMaintenance: null
              },
              configuration: {
                maxPower: charger.power_rating / 1000 || 22,
                energyPrice: charger.price_per_kwh || 0.35,
                sessionTimeout: 240,
                autoStart: true,
                rfidEnabled: true,
                contactlessEnabled: true,
                networkMode: "ethernet"
              }
            };
          });
          
          setStations(formattedStations);
        } else {
          console.error("Failed to load stations data");
        }
      } catch (error) {
        console.error("Failed to load stations:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadStationsData();
    // Refresh every 30 seconds
    const interval = setInterval(loadStationsData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'green';
      case 'offline': return 'red';
      case 'maintenance': return 'yellow';
      case 'error': return 'red';
      default: return 'gray';
    }
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available': return 'green';
      case 'in-use': return 'blue';
      case 'out-of-order': return 'red';
      case 'maintenance': return 'yellow';
      case 'unavailable': return 'gray';
      default: return 'gray';
    }
  };

  const filteredStations = stations.filter(station => {
    const matchesSearch = searchTerm === '' || 
      station.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      station.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      station.serialNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || station.status === filterStatus;
    
    const matchesConnector = filterConnector === 'all' || station.connectorType === filterConnector;
    
    const matchesPower = !filterMinPower || station.powerRating >= parseFloat(filterMinPower);
    
    return matchesSearch && matchesStatus && matchesConnector && matchesPower;
  });

  const StationCard = ({ station }) => {
    const statusColor = getStatusColor(station.status);
    const availabilityColor = getAvailabilityColor(station.availability);
    
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className={`w-12 h-12 bg-${statusColor}-100 rounded-lg flex items-center justify-center`}>
              <Zap className={`w-6 h-6 text-${statusColor}-600`} />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-gray-900">{station.name}</h3>
              <p className="text-sm text-gray-500">{station.serialNumber}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {openIssueCounts[station.id] > 0 && (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 cursor-pointer"
                title="Open rider-reported issues — click to view"
                onClick={() => setSelectedStation(station)}
              >
                <AlertTriangle className="w-3 h-3" /> {openIssueCounts[station.id]}
              </span>
            )}
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-${statusColor}-100 text-${statusColor}-800`}>
              {station.status}
            </span>
            <button
              onClick={() => setSelectedStation(station)}
              className="text-gray-400 hover:text-gray-600"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="w-4 h-4 mr-2" />
            {station.location}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm">
              <Power className="w-4 h-4 mr-2 text-blue-500" />
              <span className="font-medium">{station.powerRating}kW</span>
              <span className="text-gray-500 ml-1">{station.connectorType}</span>
            </div>
            <div className="text-sm font-medium text-gray-900">
              £{station.pricePerKwh}/kWh
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className={`flex items-center text-sm text-${availabilityColor}-600`}>
              {station.availability === 'available' ? (
                <CheckCircle className="w-4 h-4 mr-1" />
              ) : station.availability === 'in-use' ? (
                <Battery className="w-4 h-4 mr-1" />
              ) : (
                <XCircle className="w-4 h-4 mr-1" />
              )}
              {station.availability.replace('-', ' ')}
            </div>
            {station.status === 'online' ? (
              <Wifi className="w-4 h-4 text-green-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-500" />
            )}
          </div>

          {station.currentSession && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-blue-900">Active Session</span>
                <span className="text-blue-600">{station.currentSession.currentPower}kW</span>
              </div>
              <div className="text-xs text-blue-700 mt-1">
                {station.currentSession.energyDelivered}kWh delivered
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200">
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">{station.totalSessions}</p>
              <p className="text-xs text-gray-500">Total Sessions</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">£{station.totalRevenue.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Revenue</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <button 
            onClick={() => setSelectedStation(station)}
            className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <Eye className="w-4 h-4 mr-1" />
            View Details
          </button>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => openEditModal(station)}
              className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"
              title="Edit charger"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handleSettings(station.id)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              title="Commands"
            >
              <Settings className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handleMaintenance(station.id)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              title="Maintenance mode"
            >
              <Wrench className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const StationDetailModal = () => {
    const [activeTab, setActiveTab] = useState("overview");
    if (!selectedStation) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
        <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">{selectedStation.name}</h2>
            <button 
              onClick={() => setSelectedStation(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {['overview', 'issues', 'diagnostics', 'configuration', 'maintenance'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm capitalize flex items-center gap-1.5 ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab}
                  {tab === 'issues' && openIssueCounts[selectedStation.id] > 0 && (
                    <span className="bg-red-100 text-red-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{openIssueCounts[selectedStation.id]}</span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Station Info</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Serial Number:</span>
                        <span className="font-medium">{selectedStation.serialNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Power Rating:</span>
                        <span className="font-medium">{selectedStation.powerRating}kW</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Connector Type:</span>
                        <span className="font-medium">{selectedStation.connectorType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Price per kWh:</span>
                        <span className="font-medium">£{selectedStation.pricePerKwh}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Performance</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Uptime:</span>
                        <span className="font-medium">{selectedStation.uptime.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Total Sessions:</span>
                        <span className="font-medium">{selectedStation.totalSessions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Total Revenue:</span>
                        <span className="font-medium">£{selectedStation.totalRevenue.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Last Heartbeat:</span>
                        <span className="font-medium">
                          {selectedStation.lastHeartbeat ? new Date(selectedStation.lastHeartbeat).toLocaleTimeString() : "Never"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Current Status</h4>
                    <div className="space-y-3">
                      <div className={`flex items-center text-sm text-${getStatusColor(selectedStation.status)}-600`}>
                        {selectedStation.status === 'online' ? (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-2" />
                        )}
                        Status: {selectedStation.status}
                      </div>
                      <div className={`flex items-center text-sm text-${getAvailabilityColor(selectedStation.availability)}-600`}>
                        {selectedStation.availability === 'available' ? (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        ) : selectedStation.availability === 'in-use' ? (
                          <Battery className="w-4 h-4 mr-2" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-2" />
                        )}
                        {selectedStation.availability.replace('-', ' ')}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedStation.currentSession && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-3">Current Session</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-blue-600">Session ID:</span>
                        <p className="font-medium">{selectedStation.currentSession.id}</p>
                      </div>
                      <div>
                        <span className="text-blue-600">User:</span>
                        <p className="font-medium">{selectedStation.currentSession.userName}</p>
                      </div>
                      <div>
                        <span className="text-blue-600">Energy Delivered:</span>
                        <p className="font-medium">{selectedStation.currentSession.energyDelivered}kWh</p>
                      </div>
                      <div>
                        <span className="text-blue-600">Current Power:</span>
                        <p className="font-medium">{selectedStation.currentSession.currentPower}kW</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'issues' && (
              <div className="space-y-3">
                {issuesLoading ? (
                  <p className="text-center text-gray-400 py-8">Loading...</p>
                ) : chargerIssues.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">No issues reported for this charger.</p>
                ) : (
                  chargerIssues.map(issue => (
                    <div key={issue.id} className={`rounded-lg border p-4 ${issue.status === 'open' ? 'border-red-200 bg-red-50' : issue.status === 'investigating' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-semibold text-gray-900 capitalize">{issue.issue_type.replace(/_/g, ' ')}</span>
                          <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${issue.status === 'open' ? 'bg-red-100 text-red-700' : issue.status === 'investigating' ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'}`}>{issue.status}</span>
                        </div>
                        <span className="text-xs text-gray-400">{new Date(issue.created_at).toLocaleString('en-GB')}</span>
                      </div>
                      {issue.description && <p className="text-sm text-gray-700 mb-2">{issue.description}</p>}
                      <p className="text-xs text-gray-500 mb-3">
                        Reported by {issue.reporter_first_name ? `${issue.reporter_first_name} ${issue.reporter_last_name || ''}` : 'a rider'} {issue.reporter_email ? `(${issue.reporter_email})` : ''}
                      </p>
                      {issue.status !== 'resolved' && (
                        <div className="flex gap-2">
                          {issue.status === 'open' && (
                            <button onClick={() => updateIssueStatus(issue.id, 'investigating')} className="text-xs font-medium px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-100">
                              Mark Investigating
                            </button>
                          )}
                          <button onClick={() => updateIssueStatus(issue.id, 'resolved')} className="text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 text-green-700 hover:bg-green-100">
                            Mark Resolved
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'diagnostics' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <Gauge className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Voltage</p>
                    <p className="text-lg font-semibold">{selectedStation?.diagnostics?.voltage ? selectedStation.diagnostics.voltage.toFixed(1) + "V" : "Not Available"}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <Power className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Power Output</p>
                    <p className="text-lg font-semibold">{selectedStation?.diagnostics?.powerOutput ? selectedStation.diagnostics.powerOutput.toFixed(1) + "kW" : "Not Available"}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <Activity className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Current</p>
                    <p className="text-lg font-semibold">{selectedStation?.diagnostics?.current ? selectedStation.diagnostics.current.toFixed(1) + "A" : "Not Available"}</p>
                  </div>
                </div>

                {selectedStation.diagnostics.faultCodes.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-900 mb-2">Active Faults</h4>
                    <div className="space-y-2">
                      {selectedStation.diagnostics.faultCodes.map((fault, index) => (
                        <div key={index} className="flex items-center text-sm text-red-700">
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          {fault}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'configuration' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Power & Pricing</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Max Power (kW)</label>
                        <input 
                          type="number" 
                          value={selectedStation.configuration.maxPower}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                          readOnly
                        />
                        <p className="text-xs text-gray-400 mt-1">Set by hardware — cannot be changed here</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Energy Price (£/kWh)</label>
                        <input 
                          type="number" 
                          step="0.01"
                          defaultValue={selectedStation.configuration.energyPrice}
                          id="config-price"
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Location</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Address / Description</label>
                        <input 
                          type="text"
                          defaultValue={selectedStation.location}
                          id="config-location"
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Latitude</label>
                          <input 
                            type="number"
                            step="0.000001"
                            defaultValue={selectedStation.coordinates.lat}
                            id="config-lat"
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Longitude</label>
                          <input 
                            type="number"
                            step="0.000001"
                            defaultValue={selectedStation.coordinates.lng}
                            id="config-lng"
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-500">Session settings (timeout, RFID, contactless) are configured on the charger hardware via OCPP.</p>
                  <button
                    onClick={() => {
                      const price = document.getElementById('config-price')?.value;
                      const location = document.getElementById('config-location')?.value;
                      const lat = document.getElementById('config-lat')?.value;
                      const lng = document.getElementById('config-lng')?.value;
                      handleSaveConfig(selectedStation.id, {
                        energyPrice: parseFloat(price) || selectedStation.configuration.energyPrice,
                        location: location || selectedStation.location,
                        latitude: parseFloat(lat) || selectedStation.coordinates.lat,
                        longitude: parseFloat(lng) || selectedStation.coordinates.lng
                      });
                    }}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Save Configuration
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'maintenance' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Maintenance Schedule</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Last Maintenance:</span>
                        <span className="font-medium">
                          {selectedStation?.diagnostics?.lastMaintenance ? selectedStation.diagnostics.lastMaintenance.toLocaleDateString() : "Not Available"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Next Maintenance:</span>
                        <span className="font-medium">
                          {selectedStation?.diagnostics?.nextMaintenance ? selectedStation.diagnostics.nextMaintenance.toLocaleDateString() : "Not Scheduled"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Firmware Version:</span>
                        <span className="font-medium">{selectedStation.firmwareVersion}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button disabled title="Not available yet — needs OCPP scheduling support on the backend" className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-400 rounded-lg cursor-not-allowed">
                      <Wrench className="w-4 h-4 mr-2" />
                      Schedule Maintenance (coming soon)
                    </button>
                    {(!diagnosticsStatus || ['uploaded', 'upload_failed'].includes(diagnosticsStatus.status)) && (
                      <button
                        onClick={() => handleRequestDiagnostics(selectedStation.id)}
                        disabled={diagnosticsLoading}
                        className="w-full flex items-center justify-center px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {diagnosticsLoading ? 'Requesting...' : diagnosticsStatus?.status === 'uploaded' ? 'Request New Logs' : 'Download Logs'}
                      </button>
                    )}
                    {diagnosticsStatus && ['requested', 'uploading'].includes(diagnosticsStatus.status) && (
                      <div className="w-full flex items-center justify-between px-4 py-2 border border-blue-200 bg-blue-50 text-blue-700 rounded-lg text-sm">
                        <span>{diagnosticsStatus.status === 'uploading' ? 'Charger is uploading logs...' : 'Waiting for charger to upload logs...'}</span>
                        <button onClick={() => fetchDiagnosticsStatus(selectedStation.id)} className="text-blue-600 hover:text-blue-800">
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {diagnosticsStatus?.status === 'uploaded' && (
                      <button
                        onClick={() => handleDownloadDiagnostics(diagnosticsStatus.id)}
                        className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Log File
                      </button>
                    )}
                    {diagnosticsStatus?.status === 'upload_failed' && (
                      <p className="text-xs text-red-600 text-center">Charger reported the log upload failed. Try requesting again.</p>
                    )}
                    <button disabled title="Not built — would need a firmware binary from BSEC and confirmed OCPP UpdateFirmware support on the hardware; not worth building pre-launch without either. Firmware version above is real, read from the charger's own BootNotification." className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-400 rounded-lg cursor-not-allowed">
                      <Upload className="w-4 h-4 mr-2" />
                      Update Firmware (not available)
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      

      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Charging Stations</h1>
              <p className="text-sm text-gray-600">{filteredStations.length} stations found</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                  </div>
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`p-2 rounded-lg ${viewMode === 'map' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <MapPin className="w-4 h-4" />
                </button>
              </div>
              <button onClick={() => setShowAddModal(true)} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Station
              </button>
              <button 
                onClick={() => window.location.reload()}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading stations data...</span>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search stations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="maintenance">Maintenance</option>
          </select>
          <div className="flex items-center space-x-2">
            <button onClick={handleMoreFilters} className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100">
              <Filter className="w-4 h-4 mr-2" />
              {showFilterPanel ? 'Less Filters' : 'More Filters'}
            </button>
            <button onClick={handleExport} className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100">
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilterPanel && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8 -mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Connector type</label>
                <select
                  value={filterConnector}
                  onChange={(e) => setFilterConnector(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All connectors</option>
                  <option value="Type 2">Type 2</option>
                  <option value="CCS">CCS</option>
                  <option value="CHAdeMO">CHAdeMO</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min power (kW)</label>
                <input
                  type="number"
                  placeholder="e.g. 22"
                  value={filterMinPower}
                  onChange={(e) => setFilterMinPower(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => { setFilterStatus('all'); setSearchTerm(''); setFilterConnector('all'); setFilterMinPower(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Online Stations</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stations.filter(s => s.status === 'online').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100">
                <Battery className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Sessions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stations.filter(s => s.currentSession).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-red-100">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Faulted Stations</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stations.filter(s => s.diagnostics.faultCodes.length > 0).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-100">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Uptime</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stations.length > 0 ? (stations.reduce((acc, s) => acc + s.uptime, 0) / stations.length).toFixed(1) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stations Grid */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStations.map((station) => (
              <StationCard key={station.id} station={station} />
            ))}
          </div>
        )}

        {/* Map View */}
        {viewMode === 'map' && (
          <MapView stations={filteredStations} getStatusColor={getStatusColor} onStationClick={(s) => setSelectedStation(s)} />
        )}

        {/* Station Detail Modal */}
        {selectedStation && <StationDetailModal />}

        {/* Edit Charger Modal */}
        {showEditModal && editingStation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000] p-4" onClick={closeEditModal}>
            <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-1 text-gray-900">Edit Charger</h3>
              <p className="text-sm text-gray-500 mb-5">{editingStation.serial_number || `#${editingStation.id}`}</p>
              {editError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{editError}</div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Charger name"
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editForm.location}
                      onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 123 High Street, Stevenage"
                      maxLength={200}
                    />
                    <button type="button" onClick={handleEditLookupCoordinates} disabled={editGeocoding}
                      className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50 whitespace-nowrap">
                      {editGeocoding ? 'Looking up...' : 'Look up coordinates'}
                    </button>
                  </div>
                  {editGeocodeMessage && (
                    <p className={`text-xs mt-1 ${editGeocodeMessage.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>{editGeocodeMessage.text}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={editForm.latitude}
                      onChange={(e) => setEditForm({ ...editForm, latitude: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="51.9020"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={editForm.longitude}
                      onChange={(e) => setEditForm({ ...editForm, longitude: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="-0.2024"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price per kWh (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="5"
                    value={editForm.price_per_kwh}
                    onChange={(e) => setEditForm({ ...editForm, price_per_kwh: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.35"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={closeEditModal}
                  disabled={savingEdit}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
                >Cancel</button>
                <button
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                >{savingEdit ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Command Modal */}
        {showCommandModal && commandTarget && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Station Commands — {commandTarget.name}
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {commandLoading && (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                    <span className="text-sm text-gray-600">Sending command...</span>
                  </div>
                )}
                <button 
                  onClick={() => handleCommand(commandTarget.id, 'start_session')}
                  disabled={commandLoading}
                  className="w-full flex items-center px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-green-50 disabled:opacity-50"
                >
                  <Play className="w-5 h-5 text-green-500 mr-3" />
                  <div>
                    <p className="font-medium">Start Session</p>
                    <p className="text-sm text-gray-500">Manually start a charging session</p>
                  </div>
                </button>
                <button 
                  onClick={() => handleCommand(commandTarget.id, 'stop_session')}
                  disabled={commandLoading}
                  className="w-full flex items-center px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  <Square className="w-5 h-5 text-red-500 mr-3" />
                  <div>
                    <p className="font-medium">Stop Session</p>
                    <p className="text-sm text-gray-500">End current charging session</p>
                  </div>
                </button>
                <button 
                  onClick={() => handleCommand(commandTarget.id, 'restart')}
                  disabled={commandLoading}
                  className="w-full flex items-center px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                >
                  <RefreshCw className="w-5 h-5 text-blue-500 mr-3" />
                  <div>
                    <p className="font-medium">Restart Station</p>
                    <p className="text-sm text-gray-500">Reboot the charging station</p>
                  </div>
                </button>
                <button 
                  onClick={() => handleCommand(commandTarget.id, 'maintenance_mode')}
                  disabled={commandLoading}
                  className="w-full flex items-center px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-yellow-50 disabled:opacity-50"
                >
                  <Wrench className="w-5 h-5 text-yellow-500 mr-3" />
                  <div>
                    <p className="font-medium">Enable Maintenance Mode</p>
                    <p className="text-sm text-gray-500">Put station into maintenance mode</p>
                  </div>
                </button>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button 
                  onClick={() => { setShowCommandModal(false); setCommandTarget(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
        {/* Charger Onboarding Wizard */}
        {showAddModal && (
          <ChargerOnboarding
            onComplete={() => { setShowAddModal(false); window.location.reload(); }}
            onCancel={() => setShowAddModal(false)}
          />
        )}


      
    </div>
  );
};

export default ChargingStationManagement;
